import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Mobile bottom navigation (sections/oudh-mobile-nav.liquid).
 *
 * Publishes its height as `--oudh-mobile-nav-height` on <body> (0 when hidden at desktop widths)
 * so fixed bars above it, like the product page's sticky add-to-cart, can stack on top. Wishlist
 * delegates to the header's heart; the cart count follows cart updates the same way the header
 * bubble does.
 *
 * Slides away on scroll down and back on scroll up, like the header (same 8px jitter guard). It
 * stays put at the top and bottom of the page, while a drawer or dialog has the page locked, and
 * while focus is inside it. `--oudh-mnav-hidden` (1 / 0) on <body> lets the sticky add-to-cart bar
 * ride down with it.
 *
 * @extends {Component<{ cartCount: HTMLElement }>}
 */
class OudhMobileNav extends Component {
  #resizeObserver = new ResizeObserver(() => {
    document.body.style.setProperty('--oudh-mobile-nav-height', `${this.offsetHeight}px`);
  });

  #lastScrollY = 0;
  #ticking = false;
  /** @type {EventTarget | undefined} */
  #scrollTarget;

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    this.#lastScrollY = getScrollTop();
    if (this.dataset.hideOnScroll !== 'false') {
      this.#listenForScroll();
      scrollContainerMediaQuery.addEventListener('change', this.#onBreakpointChange);
    }
    this.addEventListener('focusin', this.#show);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.#onBreakpointChange);
    this.removeEventListener('focusin', this.#show);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.body.style.removeProperty('--oudh-mobile-nav-height');
    document.body.style.removeProperty('--oudh-mnav-hidden');
  }

  /** Horizon's scroller swaps from the document to `.page-wrapper` at 990px, so the listener follows it. */
  #listenForScroll = () => {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
  };

  #onBreakpointChange = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#listenForScroll();
    this.#lastScrollY = getScrollTop();
    this.#setHidden(false);
  };

  /** @param {boolean} hidden */
  #setHidden(hidden) {
    if (this.hasAttribute('data-hidden') === hidden) return;
    this.toggleAttribute('data-hidden', hidden);
    document.body.style.setProperty('--oudh-mnav-hidden', hidden ? '1' : '0');
  }

  #show = () => this.#setHidden(false);

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;

    requestAnimationFrame(() => {
      this.#ticking = false;
      const y = getScrollTop();
      const delta = y - this.#lastScrollY;

      // Sub-8px jitter (inertia, iOS rubber-band) would make the bar flicker.
      if (Math.abs(delta) < 8) return;
      this.#lastScrollY = y;

      const atTop = y <= 100;
      const atEnd = window.innerHeight + y >= document.documentElement.scrollHeight - 80;
      // Horizon's lock is `scroll-lock` on <html>, set while a drawer or dialog is open.
      const locked = document.documentElement.hasAttribute('scroll-lock');

      this.#setHidden(delta > 0 && !atTop && !atEnd && !locked && !this.querySelector(':focus-visible'));
    });
  };

  /**
   * Opens the wishlist: WishlistSuite's header heart when the app has placed one, else the
   * theme's own header link, else the app's wishlist page, else the configured URL.
   */
  wishlist() {
    const slot = document.querySelector('oudh-header [data-wl-slot]');
    const trigger = slot?.querySelector('a, button') ?? slot ?? document.querySelector('[data-oudh-wishlist-link]');
    if (trigger instanceof HTMLElement) {
      trigger.click();
      return;
    }

    // The app is set to "Header" launch + "page" display, and its header heart never lands in our
    // custom header, so in practice this is the path: the wishlist page the app created.
    const appPage = /** @type {{ WishlistGlobal?: { wishlistPageUrl?: string } }} */ (window).WishlistGlobal?.wishlistPageUrl;
    const url = appPage || this.dataset.wishlistUrl;
    if (url) window.location.href = url;
  }

  /**
   * The event fires when the request goes out; the count comes off its promise.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const { promise } = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);
    const { cartCount } = this.refs;
    if (!cartCount) return;

    promise
      ?.then((/** @type {{ cart?: { totalQuantity?: number }, detail?: { itemCount?: number } }} */ result) => {
        cartCount.textContent = String(result?.cart?.totalQuantity ?? result?.detail?.itemCount ?? 0);
      })
      .catch(() => {
        // A stale count beats an unhandled rejection; the next page load corrects it.
      });
  };
}

if (!customElements.get('oudh-mobile-nav')) {
  customElements.define('oudh-mobile-nav', OudhMobileNav);
}
